
import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import { db, FieldValue, Query } from '@/firebase';
import { ROUTER } from '@/router/constants';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { Runnit, RUNNIT_TYPE, RUNNITS_ACCESS_LEVEL } from '@run-diffusion/shared';
import {
	ADMIN_FEATURED_TEMPLATES_LIMIT,
	FEATURED_TEMPLATES_CAROUSEL_LIMIT,
	buildAdminFeaturedTemplatesQuery,
	featuredVisibilityIssue,
	nextFeaturedOrder,
	renumberFeaturedOrder,
	sortByFeaturedOrder,
} from '@/utils/featuredTemplates';

const CANDIDATE_LIMIT: number = 500;

/**
 * Admin > Featured Templates.
 *
 * The curated order behind three user-facing surfaces: the Boards carousel,
 * Home's Featured toggle, and the Templates page's Featured filter. All three
 * order by featuredOrder ascending, so this page IS that order.
 *
 * Reads Firestore directly rather than the store binding, because an admin has
 * to see featured templates users cannot: soft-deleted ones, and (before the
 * backfill) ones with no order at all. The admin query therefore drops the
 * deletedAt filter and does no orderBy, since the composite index is
 * SPARSE_ALL and would hide exactly the documents that need fixing. Sorting
 * happens in memory.
 *
 * The candidate picker is limited to templates this admin can read under
 * firestore.rules (their own, and their team's). Featured templates are
 * RunDiffusion-authored, so that is the whole realistic set; the board menu's
 * Feature action remains the escape hatch for anything else.
 */
export default Vue.extend({
	name: 'AdminFeaturedTemplates',
	data () {
		return {
			loading: true as boolean,
			saving: false as boolean,
			saveState: 'idle' as string,
			mode: 'admin' as string,
			featured: [] as Runnit[],
			readable: [] as Runnit[],
			query: '' as string,
			dragId: null as string | null,
			savedTimer: null as any,
		};
	},
	computed: {
		...mapState(['user', 'team']),
		adminMode (): boolean {
			return this.mode === 'admin';
		},
		saveIcon (): string {
			if (this.saveState === 'saving') return 'mdi-loading';
			if (this.saveState === 'saved') return 'mdi-check-circle-outline';
			return 'mdi-cloud-check-outline';
		},
		saveLabel (): string {
			if (this.saveState === 'saving') return 'Saving';
			if (this.saveState === 'saved') return 'Saved';
			return 'All changes saved';
		},
		/** The curated list with the admin's extra state attached. */
		entries (): any[] {
			return sortByFeaturedOrder(this.featured).map((runnit: Runnit) => ({
				runnit,
				issue: featuredVisibilityIssue(runnit),
			}));
		},
		/**
		 * User preview drops exactly what the user query drops: soft-deleted
		 * templates, templates with no order (the sparse index skips them), and
		 * anything past the carousel limit.
		 */
		visibleEntries (): any[] {
			if (this.adminMode) return this.entries;
			return this.entries
				.filter((entry: any) => !entry.issue)
				.slice(0, FEATURED_TEMPLATES_CAROUSEL_LIMIT);
		},
		issueCount (): number {
			return this.entries.filter((entry: any) => !!entry.issue).length;
		},
		/** Readable templates that are not featured yet, newest first. */
		candidates (): Runnit[] {
			const featuredIds: string[] = this.featured.map((runnit: Runnit) => runnit.id);
			const search: string = (this.query || '').trim().toLowerCase();
			return this.readable
				.filter((runnit: Runnit) => featuredIds.indexOf(runnit.id) < 0)
				.filter((runnit: Runnit) => (
					!search || ((runnit.title || '') + ' ' + (runnit.description || '')).toLowerCase().indexOf(search) >= 0
				))
				.slice(0, 60);
		},
	},
	mounted () {
		this.initialize();
	},
	beforeDestroy () {
		clearTimeout(this.savedTimer);
	},
	methods: {
		...mapActions(['updateSnackbar']),
		goBack () {
			this.routerPush(this.$route, this.$router, { name: ROUTER.SESSIONS });
		},
		async initialize () {
			this.loading = true;
			try {
				await Promise.all([this.fetchFeatured(), this.fetchReadableTemplates()]);
			} catch (err) {
				this.snack(SNACKBAR_STATUS.ERROR, 'Failed to load the featured templates.');
			} finally {
				this.loading = false;
			}
		},
		async fetchFeatured () {
			const featuredRef: Query = buildAdminFeaturedTemplatesQuery(
				db.collection('runnits'),
				ADMIN_FEATURED_TEMPLATES_LIMIT,
			);
			const snap = await featuredRef.get();
			this.featured = snap.docs.map((doc: any) => ({ ...doc.data(), id: doc.id })) as Runnit[];
		},
		/**
		 * Every template this admin is allowed to read: their personal ones, and
		 * their current team's. Split by access level because those are the
		 * shapes the existing composite indexes cover.
		 */
		async fetchReadableTemplates () {
			const user: any = this.user;
			const team: any = this.team;
			if (!user) {
				this.readable = [];
				return;
			}

			const queries: Query[] = [
				// index created: runnits - deletedAt Ascending teamId Ascending type Ascending userId Ascending createdAt Descending __name__ Descending
				db.collection('runnits')
					.where('userId', '==', user.id)
					.where('teamId', '==', null)
					.where('deletedAt', '==', null)
					.where('type', '==', RUNNIT_TYPE.TEMPLATE)
					.orderBy('createdAt', 'desc')
					.limit(CANDIDATE_LIMIT),
			];

			if (team && team.id) {
				// Five equality filters plus one orderBy. No single composite index
				// covers that shape; Firestore merges indexes for equality-only
				// filter sets, which was verified against production rather than
				// assumed. Adding an equality filter here is therefore safe, but
				// changing the orderBy field is not.
				queries.push(
					db.collection('runnits')
						.where('teamId', '==', team.id)
						.where('accessLevel', '==', RUNNITS_ACCESS_LEVEL.PRIVATE)
						.where('deletedAt', '==', null)
						.where('type', '==', RUNNIT_TYPE.TEMPLATE)
						.where('userId', '==', user.id)
						.orderBy('createdAt', 'desc')
						.limit(CANDIDATE_LIMIT),
				);
				// index created: runnits - accessLevel Ascending deletedAt Ascending teamId Ascending type Ascending createdAt Descending __name__ Descending
				[RUNNITS_ACCESS_LEVEL.RESTRICTED, RUNNITS_ACCESS_LEVEL.SHARED].forEach((accessLevel: any) => {
					queries.push(
						db.collection('runnits')
							.where('teamId', '==', team.id)
							.where('accessLevel', '==', accessLevel)
							.where('deletedAt', '==', null)
							.where('type', '==', RUNNIT_TYPE.TEMPLATE)
							.orderBy('createdAt', 'desc')
							.limit(CANDIDATE_LIMIT),
					);
				});
			}

			const snaps = await Promise.all(queries.map((query: Query) => query.get()));
			const byId: Record<string, Runnit> = {};
			snaps.forEach((snap: any) => {
				snap.docs.forEach((doc: any) => {
					byId[doc.id] = { ...doc.data(), id: doc.id } as Runnit;
				});
			});
			this.readable = Object.keys(byId).map((id: string) => byId[id]);
		},
		onDragStart (runnit: Runnit, event: DragEvent) {
			if (!this.adminMode) return;
			this.dragId = runnit.id;
			// Firefox refuses to start a drag unless dataTransfer carries something,
			// so without this the whole reorder is a silent no-op there.
			if (event.dataTransfer) {
				event.dataTransfer.setData('text/plain', runnit.id);
				event.dataTransfer.effectAllowed = 'move';
			}
		},
		async onDrop (target: Runnit) {
			const sourceId: string | null = this.dragId;
			this.dragId = null;
			if (!sourceId || sourceId === target.id) return;

			const ordered: Runnit[] = this.entries.map((entry: any) => entry.runnit);
			const from: number = ordered.findIndex((runnit: Runnit) => runnit.id === sourceId);
			const to: number = ordered.findIndex((runnit: Runnit) => runnit.id === target.id);
			if (from < 0 || to < 0) return;
			ordered.splice(to, 0, ordered.splice(from, 1)[0]);

			// Dense renumber, so the order never depends on the gaps left behind by
			// de-featuring. Only the documents whose position actually moved are
			// written: dragging one card normally shifts a handful, not the set.
			const updates: { id: string; featuredOrder: number }[] = renumberFeaturedOrder(ordered)
				.filter((update: { id: string; featuredOrder: number }) => {
					const current: Runnit | undefined = this.featured.find((runnit: Runnit) => runnit.id === update.id);
					return !current || current.featuredOrder !== update.featuredOrder;
				});
			if (!updates.length) return;

			this.beginSave();
			try {
				// One atomic batch rather than N independent updates, so a partial
				// failure cannot leave the list half-renumbered with duplicate
				// positions. ADMIN_FEATURED_TEMPLATES_LIMIT is 500, which is exactly
				// the batch cap.
				const batch = db.batch();
				updates.forEach((update: { id: string; featuredOrder: number }) => {
					batch.update(db.doc('runnits/' + update.id), { featuredOrder: update.featuredOrder });
				});
				await batch.commit();
				await this.fetchFeatured();
			} catch (err) {
				this.snack(SNACKBAR_STATUS.ERROR, 'Failed to reorder the featured templates.');
			} finally {
				this.endSave();
			}
		},
		async feature (runnit: Runnit) {
			this.beginSave();
			try {
				// isFeatured and featuredOrder in one write: a featured template
				// with no order is dropped by the carousel's sparse index.
				await db.doc('runnits/' + runnit.id).update({
					isFeatured: true,
					featuredOrder: nextFeaturedOrder(this.featured),
				});
				await this.fetchFeatured();
				this.query = '';
			} catch (err) {
				this.snack(SNACKBAR_STATUS.ERROR, 'Failed to feature the template.');
			} finally {
				this.endSave();
			}
		},
		async unfeature (runnit: Runnit) {
			this.beginSave();
			try {
				// Deleting featuredOrder rather than zeroing it keeps "featured with
				// no order" impossible by construction.
				await db.doc('runnits/' + runnit.id).update({
					isFeatured: false,
					featuredOrder: FieldValue.delete(),
				});
				await this.fetchFeatured();
				await this.fetchReadableTemplates();
			} catch (err) {
				this.snack(SNACKBAR_STATUS.ERROR, 'Failed to remove the template from the carousel.');
			} finally {
				this.endSave();
			}
		},
		beginSave () {
			this.saving = true;
			this.saveState = 'saving';
			clearTimeout(this.savedTimer);
		},
		endSave () {
			this.saving = false;
			this.saveState = 'saved';
			this.savedTimer = setTimeout(() => { this.saveState = 'idle'; }, 2400);
		},
		snack (status: string, message: string) {
			this.updateSnackbar({ status, message, show: true });
		},
	},
});
